import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SocialShare } from './SocialShare';

export const BlogSection = () => {
  const navigate = useNavigate();

  const articles = [
    {
      title: "The Science of Stillness — How Nature Restores the Mind",
      excerpt: "Discover how Attention Restoration Theory explains why nature heals our exhausted minds and restores our ability to focus in an overstimulated world.",
      image: "/lovable-uploads/nature-stillness-restoration.jpg",
      date: "October 20, 2025",
      readTime: "7 min read",
      author: "Mystic Forest Team",
      category: "Nature Practices",
      slug: "science-of-stillness"
    },
    {
      title: "The Gentle Rebellion of Slow Parenting: Rediscovering Presence with Our Children",
      excerpt: "In a world that glorifies productivity, choosing simplicity in parenting is a quiet rebellion. Discover the science and wisdom behind slow parenting.",
      image: "/lovable-uploads/slow-parenting-family.jpg",
      date: "October 15, 2025",
      readTime: "8 min read",
      author: "Mystic Forest Team",
      category: "Mindfulness & Lifestyle",
      slug: "slow-parenting"
    },
    {
      title: "The Shadow Side of \"Good Vibes Only\": Rethinking the Modern Wellness Culture",
      excerpt: "Wellness culture loves light. But somewhere along the way, the pursuit of wellness has turned into an obligation to feel good. Exploring toxic positivity and the path to authentic emotional health.",
      image: "/lovable-uploads/shadow-side-good-vibes.jpg",
      date: "October 2, 2025",
      readTime: "12 min read",
      author: "Piotr",
      category: "Wellness & Mental Health",
      slug: "shadow-side-good-vibes"
    },
    {
      title: "The Lost Art of Walking: Why Movement Heals the Mind",
      excerpt: "Exploring why walking is one of the most powerful tools for mental and physical health — from ancient philosophy to modern neuroscience.",
      image: "/lovable-uploads/walking-forest-road.jpg",
      date: "September 26, 2025",
      readTime: "10 min read",
      author: "Piotr",
      category: "Mindfulness & Movement",
      slug: "lost-art-of-walking"
    },
    {
      title: "The Quiet Rebellion: Why We're Choosing to Be Offline — Together",
      excerpt: "Exploring the cultural rise of intentional offline gatherings — a growing movement towards analog, in-person experiences as a counterbalance to digital saturation.",
      image: "/lovable-uploads/offline-gathering-hands.jpg",
      date: "September 22, 2025",
      readTime: "12 min read",
      author: "Piotr",
      category: "Digital Wellness",
      slug: "offline-gatherings"
    },
    {
      title: "FOMO Is Dead: The Quiet Rise of ROMO — Relief of Missing Out",
      excerpt: "Discover ROMO—the relief of missing out—and learn how conscious absence can help you reclaim presence, creativity, and peace in a noisy digital world.",
      image: "/lovable-uploads/83fe081b-c262-4d29-b438-860b88fdcd93.png",
      date: "September 14, 2025",
      readTime: "8 min read",
      author: "Piotr",
      category: "Digital Wellness",
      slug: "romo-relief"
    },
    {
      title: "Why Burnout Affects More and More People",
      excerpt: "Explore the growing burnout epidemic and discover practical pathways to prevention and recovery in our modern work culture.",
      image: "/lovable-uploads/c1bf03f6-871e-48eb-87e6-22d0c017354f.png",
      date: "August 5, 2025",
      readTime: "6 min read",
      author: "Piotr",
      category: "Mental Health",
      slug: "burnout-epidemic"
    },
    {
      title: "Digital Detox – Why Our Brain Needs Silence",
      excerpt: "Discover the transformative power of disconnecting from technology and reconnecting with yourself through mindful breaks from the digital world.",
      image: "/lovable-uploads/83fe081b-c262-4d29-b438-860b88fdcd93.png",
      date: "June 28, 2025",
      readTime: "8 min read",
      author: "Piotr",
      category: "Mindfulness",
      slug: "digital-detox"
    },
    {
      title: "The Power of Nature – Why the Forest Heals",
      excerpt: "Explore the science behind nature's healing power and learn how forest bathing can restore your mind, body, and spirit.",
      image: "/lovable-uploads/f21790ee-4570-4fde-a8c7-61222d63453d.png",
      date: "July 18, 2025",
      readTime: "9 min read",
      author: "Piotr",
      category: "Nature Practices",
      slug: "power-of-nature"
    }
  ];

  // Rotate article randomly on each page load
  const randomIndex = Math.floor(Math.random() * articles.length);
  const featuredArticle = articles[randomIndex];

  return (
    <section id="blog" className="py-24 bg-muted/20 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground">
            Echoes of the Forest
          </h2>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-accent to-transparent mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Insights and wisdom for mindful living, deep connection, and conscious presence
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
          {/* Featured Article Image */}
          <div className="order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
              <img 
                src={featuredArticle.image} 
                alt={featuredArticle.title}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105 grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>

          {/* Featured Article Content */}
          <div className="order-1 lg:order-2 space-y-6">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20">
                {featuredArticle.category}
              </Badge>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{featuredArticle.date}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{featuredArticle.readTime}</span>
              </div>
            </div>

            <h3 className="text-3xl md:text-4xl font-serif font-light text-foreground leading-tight">
              {featuredArticle.title}
            </h3>

            <p className="text-lg text-muted-foreground leading-relaxed">
              {featuredArticle.excerpt}
            </p>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span>By {featuredArticle.author}</span>
            </div>

            <div className="flex flex-col gap-4 pt-4">
              <div className="flex gap-4">
              <Button 
                onClick={() => navigate((featuredArticle.slug === 'romo-relief' || featuredArticle.slug === 'overstimulation' || featuredArticle.slug === 'offline-gatherings' || featuredArticle.slug === 'lost-art-of-walking') ? `/articles/${featuredArticle.slug}` : `/blog/${featuredArticle.slug}`)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Read Article
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    navigate('/blog');
                    setTimeout(() => window.scrollTo(0, 0), 100);
                  }}
                  className="border-border hover:bg-accent"
                >
                  View All Articles
                </Button>
              </div>
              
              <SocialShare 
                url={(featuredArticle.slug === 'romo-relief' || featuredArticle.slug === 'overstimulation' || featuredArticle.slug === 'offline-gatherings' || featuredArticle.slug === 'lost-art-of-walking') ? `https://mysticforest.club/articles/${featuredArticle.slug}` : `https://mysticforest.club/blog/${featuredArticle.slug}`}
                title={featuredArticle.title}
                description={featuredArticle.excerpt}
              />
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <p className="text-muted-foreground mb-6">
            Join our journey of mindful exploration and conscious living
          </p>
          <Button 
            size="lg"
            onClick={() => {
              navigate('/blog');
              setTimeout(() => window.scrollTo(0, 0), 100);
            }}
            className="px-12 py-4 text-lg font-medium hover-scale bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            Explore All Reflections
          </Button>
        </div>
      </div>
    </section>
  );
};